/**
 * Screen Viewer Page
 *
 * Display presentation images/video with multiple formats support:
 * - JPG snapshot (auto-refresh)
 * - MJPG stream
 * - MP4 video
 * - HLS streaming
 */

import {
	canStreamFallBackToFramePoll,
	clampPanOffset,
	followedViewerColors,
	getNativeApiUrl,
	getNativeFontScale,
	getNativeToken,
	isNativeWebView,
	isPolledSourceFormat,
	isSameApiOrigin,
	isScreenLikeSource,
	isServiceFrameUrl,
	keyboardPanForKeyDown,
	type PinchStart,
	type ProxyFetchOutcome,
	pinchPanOffset,
	proxyErrorTranslation,
	resolveApiBaseUrl,
	resolveMjpgRendering,
	ScreenFormat,
	type ScreenSource,
	type ScreenViewerSettings,
	SourceType,
	shouldProxyAdHocSource,
	streamProbeAction,
	TRANSCRIPT_VIEWER_SETTINGS_KEY,
	type ViewerColorPair,
	viewerRoomPath,
} from '@polygraf/shared'
import {
	ArrowLeft,
	Image,
	Lock,
	LockOpen,
	LogIn,
	Maximize2,
	Minimize2,
	Monitor,
	Pause,
	Play,
	RefreshCw,
	Settings,
	Video,
	WifiOff,
	X,
	ZoomIn,
	ZoomOut,
} from 'lucide-react'
import { type CSSProperties, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useIsStandaloneViewer } from '@/bootstrap'
import { SourceSelector } from '@/components/source-selector'
import { Badge, Button, buttonClasses, cn, Spinner } from '@/components/ui'
import { IphoneFullscreenHint } from '@/components/viewer/iphone-fullscreen-hint'
import { LiveKitScreenPane } from '@/components/viewer/livekit-screen-pane'
import { ViewerBreadcrumb } from '@/components/viewer/viewer-breadcrumb'
import { useBearerImageSrc } from '@/hooks/use-bearer-image-src'
import { useImmersiveDoubleTap } from '@/hooks/use-immersive-double-tap'
import { usePageTitle } from '@/hooks/use-page-title'
import { useSourceFrame } from '@/hooks/use-source-frame'
import { exitBrowserFullscreen } from '@/lib/exit-browser-fullscreen'
import { probeProxyError } from '@/lib/proxy-error'
import { trpc } from '@/trpc/client'

// API base URL for proxy. Used both as the cloud-mode prefix and the
// fallback for ad-hoc proxy endpoints — see ADR-0005.
//
// Resolution order (canonical `resolveApiBaseUrl` in `@polygraf/shared/connection`):
//   1. `getNativeApiUrl()` — the mobile WebView injects `POLYGRAF_NATIVE.apiUrl`
//      at runtime via `injectedJavaScriptBeforeContentLoaded`. The bundled
//      HTML asset is built ONCE and shipped via EAS Update without env vars,
//      so without this runtime check the viewer would freeze at the dev
//      fallback and every JPG poll would hit `http://localhost:4000` → iOS
//      WebKit can't connect → `NSURLErrorCannotConnectToHost (-1004)` →
//      blank canvas (#324 root cause).
//   2. `import.meta.env.VITE_API_URL` — set at build time on the regular
//      web build (Dockerfile passes it in for `polygraf.app`).
//   3. `http://localhost:4000` — dev fallback when nothing's set.
const API_URL = resolveApiBaseUrl(import.meta.env.VITE_API_URL, 'http://localhost:4000')

// iOS Safari (and every iOS browser, since they all wrap WebKit) cannot
// render `multipart/x-mixed-replace` streams in an `<img>` tag — the
// element shows a broken-image placeholder and onError fires. Chrome and
// Firefox on desktop + Android handle MJPG fine. Detect the platform so
// we can fall back to the room's JPG snapshot sibling when one exists.
// `maxTouchPoints > 1` catches iPad on iPadOS 13+, which advertises a
// Mac-like UA string after the desktop-class browsing change.
function isIOSWebKit(): boolean {
	if (typeof navigator === 'undefined') return false
	const ua = navigator.userAgent
	const isIOSUA = /iPad|iPhone|iPod/.test(ua)
	const isIPadOS = ua.includes('Macintosh') && (navigator.maxTouchPoints ?? 0) > 1
	return isIOSUA || isIPadOS
}

const DEFAULT_SETTINGS: ScreenViewerSettings = {
	format: ScreenFormat.JPG,
	refreshInterval: 1,
	grayscale: false,
	inverted: false,
	zoomLevel: 1.0,
	zoomLocked: false,
}

const SETTINGS_KEY = 'polygraf-screen-settings'

/**
 * The transcript viewer's colours, or `null` to keep this viewer's own.
 *
 * Wrapped because `localStorage` is not merely empty when a browser has
 * storage blocked — the accessor itself throws. This runs in a state
 * initializer, so an unguarded read would take the whole viewer down during
 * render instead of falling back to the theme surface.
 */
function readFollowedColors(): ViewerColorPair | null {
	try {
		return followedViewerColors(localStorage.getItem(TRANSCRIPT_VIEWER_SETTINGS_KEY))
	} catch {
		return null
	}
}

/**
 * Consecutive failed loads before a polled source's failure takes over the
 * whole pane instead of showing as a header badge. At the 1 s default poll
 * that is ~3 s of a genuinely unreachable source — long enough that a single
 * dropped frame never blanks the picture, short enough that a dead camera
 * still explains itself while the student is looking at it.
 */
const FAILED_LOADS_BEFORE_TAKEOVER = 3

/**
 * Shortest viewport side, in CSS pixels, at or above which the viewer treats
 * the device as a tablet and keeps its full header in landscape.
 *
 * 600 matches Android's `sw600dp` qualifier and the mobile shell's own
 * `TABLET_MIN_SHORT_SIDE_DP`: every iPad lands above it, every iPhone below.
 */
const TABLET_MIN_SHORT_SIDE_PX = 600

/**
 * Query flag set when iOS was sent to an MJPG service's JPG sibling because
 * WebKit cannot render `multipart/x-mixed-replace` in an `<img>`.
 */
const MJPG_FALLBACK_PARAM = 'mjpgFallback'

const MIN_ZOOM = 0.5
// 5.0 per MU request 7. 8. 2026 — 300 % was not enough for some low-vision
// users. Purely a presentation scale() on the already-decoded frame, so the
// only cost of a higher ceiling is pixelation the user opted into.
const MAX_ZOOM = 5.0

/**
 * Clamp + round-to-1-decimal so repeated +/- 0.1 keypresses don't accumulate
 * float error. Without this, going 1.0 → 1.1 → 1.2 → ... → 1.0 via keyboard
 * lands on something like 1.0000000000000004 which then becomes
 * `transform: scale(1.0000000000000004)`. The image is just barely larger
 * than the wrapper's `overflow-hidden` box → the edges get clipped while
 * `Math.round(zoomLevel * 100)` still reads "100%" (MP-152).
 */
function clampZoom(value: number): number {
	const clamped = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, value))
	return Math.round(clamped * 10) / 10
}

/**
 * Formats the media-relay serves to the browser as an HLS manifest: native
 * HLS plus the relayed RTSP / NDI sources (MediaMTX transcodes both to HLS).
 * They all render through the same hls.js `<video>` path — MP4 is video too
 * but plays via a native `src`, not hls.js, so it is deliberately excluded.
 */
function isHlsLikeFormat(format: ScreenFormat | string): boolean {
	return format === ScreenFormat.HLS || format === ScreenFormat.RTSP || format === ScreenFormat.NDI
}

/**
 * Infer the render format of an ad-hoc (`?url=`) source from its path
 * extension. Ad-hoc mode used to reuse whatever format the persisted viewer
 * settings held (default JPG), so manually connecting an MJPG stream polled
 * it as a JPG snapshot — the proxy's image fetch never completes on an
 * endless multipart body and the canvas stayed silently blank (MU #2.5,
 * "už nekončí chybou URL not allowed, ale nezobrazí nic"). `null` means
 * "no opinion" — keep the user's setting.
 */
function inferAdHocFormat(rawUrl: string): ScreenFormat | null {
	let pathname: string
	try {
		pathname = new URL(rawUrl).pathname.toLowerCase()
	} catch {
		return null
	}
	if (/\.(mjpg|mjpeg)$/.test(pathname)) return ScreenFormat.MJPG
	if (/\.(jpg|jpeg)$/.test(pathname)) return ScreenFormat.JPG
	if (/\.mp4$/.test(pathname)) return ScreenFormat.MP4
	if (/\.m3u8$/.test(pathname)) return ScreenFormat.HLS
	return null
}

/**
 * Bridge a media failure to the React Native shell (which forwards it to
 * Sentry). No-op outside the mobile WebView. The `source: 'screen-viewer'`
 * envelope matches what the RN `onMessage` handler already routes — the same
 * channel `screen-viewer-entry.tsx` uses for window.onerror.
 */
function postNativeMediaFailure(payload: Record<string, unknown>): void {
	try {
		const bridge = (
			window as unknown as { ReactNativeWebView?: { postMessage: (s: string) => void } }
		).ReactNativeWebView
		bridge?.postMessage(JSON.stringify({ source: 'screen-viewer', ...payload }))
	} catch {
		// Web build without the bridge, or an unserializable payload — drop it.
	}
}

export default function ScreenViewerPage() {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const location = useLocation()
	const { sourceId } = useParams<{ sourceId: string }>()
	const [searchParams] = useSearchParams()
	const urlParam = searchParams.get('url')
	/**
	 * True when this page is the JPG stand-in for an MJPG service the device
	 * cannot render. The badge then names both formats, so the badge and the
	 * service name the student started from stop contradicting each other.
	 */
	const isMjpgFallback = searchParams.get(MJPG_FALLBACK_PARAM) === '1'

	/**
	 * The transcript viewer's chosen colours, followed so the two viewers do not
	 * disagree about the colour of the page (MU, 18. 9. 2026). `null` keeps the
	 * theme-aware `viewer-surface` default, so nothing changes for a student who
	 * never touched the transcript colours.
	 *
	 * Both colours, not just the ground: painting a dark background while the
	 * header keeps a light-theme foreground would be worse than leaving it
	 * alone, and this viewer is read by the students least able to absorb that.
	 *
	 * Re-read on `storage` (changed in another tab) and when the tab is shown
	 * again — the app's two WebViews do not raise `storage` for each other.
	 */
	const [followedColors, setFollowedColors] = useState<ViewerColorPair | null>(readFollowedColors)

	/**
	 * Header badges on a followed ground.
	 *
	 * The badge tones are fixed theme colours — dark text on a 15% tint — so on a
	 * followed dark ground they composite to roughly 2.5–3:1. A reader on the
	 * light app theme with a dark transcript preset, the very reader colour
	 * following exists for, would otherwise see these drop from 5.1:1 to ~3:1.
	 * Outlining them in the followed text colour gives them the pair's own
	 * contrast, which the legibility gate already guarantees. The label and icon
	 * still carry the meaning, so nothing depends on the lost tint colour.
	 */
	const followedBadgeStyle = followedColors
		? {
				color: followedColors.textColor,
				backgroundColor: 'transparent',
				boxShadow: 'inset 0 0 0 1px currentColor',
			}
		: undefined

	/**
	 * Paint a surface with the followed colours, or leave it to the theme.
	 *
	 * Every opaque surface has to take BOTH colours together. Painting only the
	 * root left the picture pane and the settings panel on `viewer-surface`
	 * white while their text inherited the followed foreground — several of the
	 * transcript presets store a light `textColor`, so the panel rendered at
	 * roughly 1:1 contrast. That is the exact failure `legibleViewerColors`
	 * exists to prevent, so a surface either follows completely or not at all.
	 */
	const surface = useCallback(
		(base: string, themeClass = 'viewer-surface') => ({
			className: followedColors ? base : `${base} ${themeClass}`,
			style: followedColors
				? {
						backgroundColor: followedColors.backgroundColor,
						color: followedColors.textColor,
					}
				: undefined,
		}),
		[followedColors],
	)

	useEffect(() => {
		const reread = () =>
			setFollowedColors((prev) => {
				const next = readFollowedColors()
				// `visibilitychange` fires twice per app switch on mobile, and a
				// fresh object every time would re-render the whole viewer for
				// nothing.
				if (prev === next) return prev
				if (
					prev &&
					next &&
					prev.backgroundColor === next.backgroundColor &&
					prev.textColor === next.textColor
				) {
					return prev
				}
				return next
			})
		window.addEventListener('storage', reread)
		document.addEventListener('visibilitychange', reread)
		return () => {
			window.removeEventListener('storage', reread)
			document.removeEventListener('visibilitychange', reread)
		}
	}, [])

	const [settings, setSettings] = useState<ScreenViewerSettings>(() => {
		try {
			const saved = localStorage.getItem(SETTINGS_KEY)
			// The zoom lock persists on every platform: the header carries a
			// lock button (also on native portrait) and the settings panel has
			// the checkbox, so a stored lock can always be undone.
			return saved ? { ...DEFAULT_SETTINGS, ...JSON.parse(saved) } : DEFAULT_SETTINGS
		} catch {
			return DEFAULT_SETTINGS
		}
	})
	const [isLoading, setIsLoading] = useState(true)
	// True once the <video> has decoded its first frame. For HLS-like formats the
	// manifest URL resolves before any media is buffered (e.g. while an on-demand
	// relay source warms up), so `isLoading` clears too early and the pane would
	// sit visibly blank; this drives a warm-up spinner *over* the video until a
	// real frame arrives (onLoadedData). Reset whenever the source URL changes.
	const [videoReady, setVideoReady] = useState(false)
	// The <img> twin of `videoReady`, and for the same reason: `isLoading` goes
	// false as soon as a URL *string* exists, which for a proxied source is
	// instant, so it cannot say whether a frame has actually arrived. Without
	// this the pane sat blank with no spinner for as long as the browser was
	// willing to wait on a dead source (~10 s on the MUNI cameras), then painted
	// the browser's own broken-image glyph — reading as a broken app rather than
	// a source that stopped answering.
	const [imageReady, setImageReady] = useState(false)
	/**
	 * Page-level failure: no such service, not permitted, no usable endpoint, a
	 * fatal LiveKit room error. Owned by the resolution effect below; survives a
	 * source change because it usually IS the reason the source changed.
	 */
	const [error, setError] = useState<string | null>(null)
	/**
	 * Element-level failure: the `<img>`/`<video>` couldn't load this source.
	 * Deliberately NOT the same state as `error` — it belongs to one source and
	 * must be dropped when the viewer switches to a sibling, whereas clearing
	 * `error` on a source change would clobber an auth failure raised by the
	 * resolution effect in the same commit.
	 */
	const [loadError, setLoadError] = useState<string | null>(null)
	/**
	 * Consecutive failed loads for the current source. A JPG source re-polls
	 * every second, so one bad tick is the normal shape of a flaky camera, not
	 * news — see `errorOwnsPane`.
	 */
	const [failedLoads, setFailedLoads] = useState(0)
	const [isFullscreen, setIsFullscreen] = useState(false)
	/**
	 * Immersive mode: a tap on the picture hides every control and leaves the
	 * image alone on screen (MU 6. 8. 2026 — the old mobile app had this in
	 * both viewers). Tap again to bring the controls back; Escape does the
	 * same for keyboard users. Not the Fullscreen API (`isFullscreen`): that
	 * one cannot be entered from inside the mobile app's WebView.
	 */
	const [immersive, setImmersive] = useState(false)
	// Settings panel under the header (filters, zoom, zoom lock, connection status) — the
	// transcript viewer's pattern. Replaces the footer row and the coloured
	// header toggles MU asked to drop (5. 8., reopened 25. 8.).
	const [showSettings, setShowSettings] = useState(false)
	// MP-187: presenter's display name from the LiveKit pane, shown in the header.
	const [presenterName, setPresenterName] = useState<string | null>(null)
	const [isPlaying, setIsPlaying] = useState(true)
	const [source, setSource] = useState<ScreenSource | null>(null)
	const [useProxy, setUseProxy] = useState(false)

	// Ad-hoc sources declare their format in the URL, not in the persisted
	// viewer settings — see inferAdHocFormat. Runs per URL; the user can still
	// override via the settings modal afterwards (deps deliberately exclude
	// settings.format so this never fights a manual choice).
	// biome-ignore lint/correctness/useExhaustiveDependencies: intentional — re-infer only when the ad-hoc URL changes.
	useEffect(() => {
		if (!urlParam) return
		const inferred = inferAdHocFormat(urlParam)
		if (inferred) {
			setSettings((prev) => (prev.format === inferred ? prev : { ...prev, format: inferred }))
		}
	}, [urlParam])

	const containerRef = useRef<HTMLDivElement>(null)
	const videoRef = useRef<HTMLVideoElement>(null)

	// LiveKit room connection, reported by the pane. The header badge used to
	// read the JPG-polling `isConnected`, which is always false during a
	// browser share — the permanent "Nepřipojeno" MU reported (5. 8.).
	const [liveKitConnected, setLiveKitConnected] = useState(false)

	// Drive the <video> element from the toolbar's play/pause state. The
	// toggle used to flip only the `autoPlay` ATTRIBUTE, which browsers
	// consult once at element load — so for video formats the button did
	// nothing at all (JPG worked because `isPlaying` separately gates the
	// poll interval). The ref mirror lets media-lifecycle callbacks
	// (onLoadedData after an engine rebuild) read the CURRENT intent
	// without re-subscribing.
	const isPlayingRef = useRef(isPlaying)
	// WebKit refused a programmatic play() — after a declined autoplay (Low
	// Power Mode, or iOS treating the WKWebView load as non-user-initiated)
	// only a REAL user gesture may start playback. The element deliberately
	// carries `pointer-events-none` (a stray tap must never pause a lecture),
	// which also makes WebKit's own centered play glyph untappable — so we
	// render our own tap-to-play overlay whose click IS the gesture.
	const [needsUserPlay, setNeedsUserPlay] = useState(false)
	const playVideo = useCallback((video: HTMLVideoElement) => {
		video
			.play()
			.then(() => setNeedsUserPlay(false))
			.catch((err: unknown) => {
				if ((err as { name?: string } | null)?.name === 'NotAllowedError') {
					setNeedsUserPlay(true)
				}
			})
	}, [])
	useEffect(() => {
		isPlayingRef.current = isPlaying
		const video = videoRef.current
		if (!video) return
		if (isPlaying) {
			playVideo(video)
		} else {
			video.pause()
		}
	}, [isPlaying, playVideo])

	// Fetch service info with siblings for source selector.
	// Only fetch if we have a sourceId but no URL param (cloud mode).
	//
	// MP-180: poll while the tab is open. `liveSessionId` is the live-vs-static
	// source-selection input — without refetching, a device that opened the
	// viewer BEFORE the presenter started a web broadcast kept rendering the
	// service's static `endpoint` (on the demo room: the teststream countdown)
	// forever, while devices opened after the start showed the share. Same 15s
	// cadence the unified viewer uses for `getByDocumentId`. The reverse
	// transition (broadcast ended → fall back to the static endpoint) rides the
	// same poll.
	const { data: serviceInfo, error: serviceInfoError } = trpc.rooms.getService.useQuery(
		{ serviceId: sourceId ?? '' },
		{
			enabled: !!sourceId && !urlParam,
			refetchInterval: 15_000,
			refetchOnWindowFocus: true,
		},
	)
	usePageTitle(serviceInfo?.name ?? t('viewer.screen.title'))

	// The service whose stream failed although a fetch of the same URL worked (see
	// `resolveMjpgRendering`). Holding the id drops it on a selector switch, which
	// does not remount this route: the next service gets its own stream attempt.
	const [unplayableStreamId, setUnplayableStreamId] = useState<string | null>(null)
	// The proxy answered 429, so polling waits this long. An object, so that a second
	// 429 restarts the wait.
	const [pollThrottle, setPollThrottle] = useState<{ ms: number } | null>(null)
	useEffect(() => {
		if (!pollThrottle) return
		const timer = setTimeout(() => setPollThrottle(null), pollThrottle.ms)
		return () => clearTimeout(timer)
	}, [pollThrottle])

	const mjpgRendering = resolveMjpgRendering({
		isIOSWebKit: isIOSWebKit(),
		// An `<img>` can't carry the app's bearer, so an MJPG stream is refused in
		// an IP_RESTRICTED space (MU, 7. 10. 2026) and in a members-only room
		// (MU, 2. 11. 2026); a bearer fetch of a frame is not.
		isNativeWithToken: isNativeWebView() && !!getNativeToken(),
		format: serviceInfo?.format,
		hasActiveJpgSibling: !!serviceInfo?.siblingServices?.some(
			(s) => s.format === 'JPG' && s.isActive,
		),
		isAdHoc: !!urlParam,
		streamFailedButFetchOk: !!sourceId && unplayableStreamId === sourceId,
	})
	// `settings.format` follows the service one effect later, and until then the
	// persisted format decides which element is mounted.
	const framePoll = mjpgRendering === 'frame-poll' && settings.format === ScreenFormat.MJPG
	// Would a failed stream here turn into frame polling? Only a viewer the rendering
	// decision would move, on a persisted service showing no sibling. While a newly
	// selected service loads, the format on screen decides.
	const canSwitchToFramePoll = canStreamFallBackToFramePoll({
		isIOSWebKit: isIOSWebKit(),
		isNativeWithToken: isNativeWebView() && !!getNativeToken(),
		viewerFormat: settings.format,
		serviceFormat: serviceInfo?.format,
		hasSourceId: !!sourceId,
		isAdHoc: !!urlParam,
	})

	// Single source-of-truth for "what URL should the renderer use" — collapses
	// the three diverging useEffects (JPG poll / MJPG single-URL / MP4-HLS
	// passthrough) that used to live inline. See the hook's docstring +
	// ADR-0005 (URL construction) for the decision tree.
	const {
		url: imageUrl,
		baseUrl: sourceBaseUrl,
		isConnected,
		lastUpdate,
	} = useSourceFrame({
		source,
		format: settings.format,
		refreshInterval: settings.refreshInterval,
		isPlaying: isPlaying && !pollThrottle,
		useProxy,
		sourceId,
		urlParam,
		apiUrl: API_URL,
		mjpgFramePoll: framePoll,
	})
	// The failed-frame report below runs once per failure streak: it goes to Sentry,
	// and a polling source fails once a second.
	const frameFailureReportedRef = useRef(false)
	const reportFrameFailure = useCallback(
		(outcome: ProxyFetchOutcome) => {
			setFailedLoads((n) => n + 1)
			if (outcome.kind === 'refused') {
				const { key, params } = proxyErrorTranslation(outcome.body)
				setLoadError(t(key, params))
				if (outcome.retryAfterMs) setPollThrottle({ ms: outcome.retryAfterMs })
			} else {
				setLoadError(t('viewer.screen.cannotLoadImage'))
			}
			if (frameFailureReportedRef.current) return
			frameFailureReportedRef.current = true
			postNativeMediaFailure({
				type: 'media-load-failure',
				message: 'viewer.screen.cannotLoadImage',
				format: ScreenFormat.MJPG,
				code: outcome.kind === 'refused' ? outcome.body.code : null,
			})
		},
		[t],
	)
	// Frame polling takes the bearer path only once the URL is a frame's: for one
	// render after the switch it is still the stream's, and fetching that would open
	// one more upstream connection for nothing.
	const imageSrc = useBearerImageSrc(
		imageUrl,
		sourceBaseUrl,
		API_URL,
		settings.format === ScreenFormat.JPG || (framePoll && isServiceFrameUrl(imageUrl)),
		framePoll ? reportFrameFailure : undefined,
	)

	// `<img>`/`<video>` report failure with a bare `error` event, so the reason
	// lives only in the response the element threw away. Show the generic
	// message immediately, then ask the proxy what actually happened and
	// upgrade to something actionable ("the source answered with a login page"
	// beats "cannot load image").
	//
	// `probeGenerationRef` is bumped by every event that makes an in-flight
	// answer obsolete — a later failure, a successful load, a source change.
	// Without the success bump, a probe fired while an on-demand relay was cold
	// resolves AFTER the rebuild ladder recovers the stream and paints a
	// permanent error badge over playing video (onLoad/onLoadedData fire once,
	// so nothing would ever clear it again).
	//
	// `probedUrlRef` keeps it to one probe per source URL: a JPG source polls
	// at `refreshInterval` (1 s by default), so probing every error event would
	// double the request rate against a source that is already down and make
	// the badge flicker between the generic and the specific message once a
	// second.
	const probeGenerationRef = useRef(0)
	const probedUrlRef = useRef<string | null>(null)
	const invalidateProbe = useCallback(() => {
		probeGenerationRef.current++
	}, [])
	// The stream of this service may be the kind of failure frame polling answers.
	// The generic message then waits for the probe, or the 401 of every app member
	// in a private room would flash an error card before the first frame.
	const mayFallBackToFramePoll = mjpgRendering === 'stream' && canSwitchToFramePoll
	const reportLoadFailure = useCallback(
		(fallbackKey: 'viewer.screen.cannotLoadImage' | 'viewer.screen.cannotPlayVideo') => {
			const showGeneric = () => setLoadError(t(fallbackKey))
			if (!mayFallBackToFramePoll) showGeneric()
			setFailedLoads((n) => n + 1)
			if (!imageUrl || probedUrlRef.current === imageUrl) {
				if (mayFallBackToFramePoll) showGeneric()
				return
			}
			probedUrlRef.current = imageUrl
			// Inside the mobile WebView, surface the failure to the RN shell —
			// it forwards to Sentry. AVFoundation failures on-device are
			// otherwise invisible: no devtools, and MP-175-family stalls emit no
			// error event at all, so the only remote evidence of "HLS/MP4 never
			// starts on the phone" is this message. Gated by the same one-shot
			// as the probe: a polling JPG source fails once per second, and a
			// per-tick bridge message would flood Sentry with duplicates.
			const media = videoRef.current
			postNativeMediaFailure({
				type: 'media-load-failure',
				message: fallbackKey,
				format: settings.format,
				src: imageUrl,
				readyState: media?.readyState ?? null,
				networkState: media?.networkState ?? null,
				mediaError: media?.error ? { code: media.error.code, message: media.error.message } : null,
			})
			const generation = ++probeGenerationRef.current
			void probeProxyError(imageUrl, API_URL).then((outcome) => {
				if (generation !== probeGenerationRef.current) return
				const action = streamProbeAction({
					outcome: outcome.kind,
					mayFallBackToFramePoll,
					hasSourceId: !!sourceId,
				})
				if (action === 'frame-poll' && sourceId) {
					// The source answers this viewer; its element is what cannot play
					// it. `sourceBaseUrl` changes with this, which clears the failure.
					setUnplayableStreamId(sourceId)
					return
				}
				if (action === 'generic') showGeneric()
				if (outcome.kind !== 'refused') return
				const { key, params } = proxyErrorTranslation(outcome.body)
				setLoadError(t(key, params))
				if (outcome.retryAfterMs) setPollThrottle({ ms: outcome.retryAfterMs })
				// The proxy's verdict is the actionable half ("upstream answered
				// 502" beats "cannot play video") — ship it to Sentry too.
				postNativeMediaFailure({
					type: 'media-probe-result',
					message: key,
					code: outcome.body.code,
				})
			})
		},
		[imageUrl, t, settings.format, mayFallBackToFramePoll, sourceId],
	)

	// A new source URL retires any in-flight probe and re-arms the one-shot.
	// biome-ignore lint/correctness/useExhaustiveDependencies: change detector — the body resets refs rather than reading imageUrl.
	useEffect(() => {
		probeGenerationRef.current++
		probedUrlRef.current = null
	}, [imageUrl])

	// A service backed by a live LiveKit session (a browser presenter sharing
	// their screen via WebRTC) has no static `endpoint` — it streams through
	// LiveKit instead. When present, the viewer subscribes to the LiveKit room
	// rather than polling a proxy URL. `urlParam` (ad-hoc direct URL mode) always
	// wins, since that's an explicit non-cloud source.
	const liveKitSessionId = !urlParam ? (serviceInfo?.liveSessionId ?? null) : null

	// MU 4. 9.: "not clear what the Play/Pause button is for … it seems to do
	// nothing". Pausing only means something for the polled JPG source, where it
	// stops the refresh interval and freezes the frame. A browser share streams
	// through LiveKit with no `<video>` ref and no poll timer, so both the button
	// and the Space shortcut were silent no-ops there. The LiveKit branch of the
	// source effect returns before the format sync below, so `settings.format`
	// keeps its persisted default (JPG) during a share — which is why the gate
	// has to test `liveKitSessionId`, not the format alone.
	//
	// `source` is the third term, found by testing the shipped fix on prod: a
	// browser-share service that nobody is sharing to right now has no session
	// AND no endpoint, so the first two terms both pass on the persisted JPG
	// default and the button came back — dead, on a viewer showing "Zdroj není
	// dostupný". Worse, pressing it raised a "Pozastaveno" badge next to
	// "Nepřipojeno". There is nothing to freeze until a source resolves.
	const canPause =
		!liveKitSessionId && (settings.format === ScreenFormat.JPG || framePoll) && source !== null

	// The source selector switches siblings without remounting this route
	// (MP-187), so a paused JPG carries `isPlaying: false` into the next source.
	// On an MP4/HLS sibling the media effect would then call `video.pause()`
	// while the gate above has already removed the only play control — the
	// lecture sits frozen with no way to resume. Resume whenever the new source
	// cannot be paused.
	useEffect(() => {
		if (!canPause) setIsPlaying(true)
	}, [canPause])

	// MP-187: the source selector navigates between siblings without remounting
	// this route, so reset the presenter name whenever the LiveKit session
	// changes — otherwise the previous presenter's name shows on the new source
	// until its pane connects and reports.
	// Reset-on-change effect: the body reads nothing, so the rule sees the dep
	// as surplus — but the dep IS the trigger. Dropping it turns this into a
	// mount-only reset and the stale presenter name comes back.
	// biome-ignore lint/correctness/useExhaustiveDependencies: the dep is the trigger, not an input
	useEffect(() => {
		setPresenterName(null)
	}, [liveKitSessionId])

	// Also taken by the signed-in native app (see shouldPreferJpgSibling).
	// iOS Safari can't render MJPG via `<img>` (WebKit dropped reliable
	// `multipart/x-mixed-replace` support years ago). When a viewer link
	// resolves to an MJPG service AND the room has an active JPG sibling,
	// route-replace to that sibling so the same content renders via plain
	// JPG polling that WebKit handles. Without a JPG sibling the stream is tried
	// first, and `framePoll` above takes over if it fails.
	useEffect(() => {
		if (!serviceInfo || mjpgRendering !== 'sibling') return
		const jpgSibling = serviceInfo.siblingServices?.find((s) => s.format === 'JPG' && s.isActive)
		if (jpgSibling) {
			// Carry the substitution into the URL. Without it the viewer lands on
			// a service whose badge says JPG while the room, the app's source list
			// and the QR the student scanned all say MJPG, with nothing on screen
			// to explain the difference (MU, 17. 9. 2026: "název služby MJPG vs
			// JPG nesoulad"). The flag survives a reload and a shared link.
			navigate(`/view/screen/${jpgSibling.id}?${MJPG_FALLBACK_PARAM}=1`, { replace: true })
		}
	}, [serviceInfo, mjpgRendering, navigate])

	// Save settings to localStorage
	useEffect(() => {
		localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings))
	}, [settings])

	// Load source from URL parameter or service info
	useEffect(() => {
		// Use explicit URL param if provided
		if (urlParam) {
			try {
				const url = new URL(urlParam)
				const newSource: ScreenSource = {
					id: sourceId || `screen-${Date.now()}`,
					name: t('viewer.screen.imageFromHost', { hostname: url.hostname }),
					format: settings.format,
					endpoint: urlParam,
					isActive: true,
				}
				// An http:// LAN source on the https web viewer is mixed content
				// the browser blocks (MU 2026-07-24 "Nelze načíst obraz") — route
				// it through the API proxy so the browser only fetches https. The
				// native app fetches cleartext LAN fine, so it stays direct.
				const needsProxy = shouldProxyAdHocSource({
					pageProtocol: window.location.protocol,
					sourceUrl: urlParam,
					isNative: isNativeWebView(),
				})
				// HLS is the one format with no ad-hoc proxy path: the generic
				// endpoints return the .m3u8 byte-for-byte, so hls.js resolves
				// every relative segment URI against the proxy URL and each one
				// 400s. Only /api/proxy/service/:id rewrites the playlist. Say so
				// instead of mounting a player that can never load — a black
				// rectangle reads as "the stream is down" and sends the user
				// hunting the wrong problem.
				if (needsProxy && settings.format === ScreenFormat.HLS) {
					setError(t('viewer.screen.adHocHlsUnsupported'))
					setIsLoading(false)
					return
				}
				setSource(newSource)
				setUseProxy(needsProxy)
				setError(null)
			} catch {
				setError(t('viewer.screen.invalidSourceAddress'))
				setIsLoading(false)
			}
			return
		}

		// LiveKit-backed service: a browser presenter is sharing via WebRTC, so
		// there is no static endpoint to resolve. Clear loading/error and let
		// `renderContent`'s LiveKit branch take over (it owns connection state).
		// Checked before the endpoint branches so the "service not configured"
		// path doesn't fire for a service that legitimately has no endpoint.
		// Also drop any previously-resolved static source — `useSourceFrame`
		// would otherwise keep polling the endpoint (e.g. teststream JPG frames)
		// in the background behind the LiveKit pane. The endpoint branch below
		// re-creates it when the broadcast ends and `liveSessionId` clears.
		if (liveKitSessionId) {
			setSource(null)
			setError(null)
			setIsLoading(false)
			return
		}

		// Cloud mode: use endpoint from service info
		if (serviceInfo?.endpoint) {
			try {
				const url = new URL(serviceInfo.endpoint)
				const serviceFormat = (serviceInfo.format as ScreenFormat) || settings.format
				const newSource: ScreenSource = {
					id: serviceInfo.id,
					name: serviceInfo.name || t('viewer.screen.imageFromHost', { hostname: url.hostname }),
					format: serviceFormat,
					endpoint: serviceInfo.endpoint,
					isActive: true,
				}
				setSource(newSource)
				setSettings((prev) => ({ ...prev, format: serviceFormat }))
				// Cloud services always route through `/api/proxy/service/:id`. The
				// viewer's network is unknown (a remote student isn't on the lecture-
				// hall LAN), and the upstream endpoint is frequently a private MUNI
				// AV host — exactly the case the server-side proxy exists for: it can
				// reach the LAN endpoint (trusted: true) and applies space-visibility
				// checks. Deciding direct-vs-proxy from the *upstream* endpoint's LAN
				// status (the old `!isLanUrl(...)`) inverted this: a LAN endpoint set
				// useProxy=false, so the browser fetched the unreachable upstream
				// directly and bypassed access control (MP-133).
				setUseProxy(true)
				setError(null)
			} catch {
				setError(t('viewer.screen.invalidSourceAddress'))
				setIsLoading(false)
			}
			return
		}

		// tRPC `getService` query failed (network, CORS, NOT_FOUND, FORBIDDEN…).
		// Without this branch the page sits on the loading spinner forever
		// because the early-return below treats "no serviceInfo" as still-pending.
		// Auth failures get a human message instead of a raw tRPC string:
		// UNAUTHORIZED additionally renders a login CTA in the error state (see
		// `loginHref` below); FORBIDDEN can mean a space-visibility or IP/network
		// rule — the server deliberately doesn't say which, so the copy hedges.
		// Everything else surfaces the underlying message so users (and Sentry)
		// can see what actually broke instead of staring at a blank canvas.
		if (sourceId && serviceInfoError) {
			const code = (serviceInfoError.data as { code?: string } | null | undefined)?.code
			if (code === 'UNAUTHORIZED') {
				setError(t('viewer.screen.loginRequired'))
			} else if (code === 'FORBIDDEN') {
				setError(t('viewer.screen.accessRestricted'))
			} else {
				setError(`${t('viewer.screen.cannotLoadService')}: ${serviceInfoError.message}`)
			}
			setIsLoading(false)
			return
		}

		// Still loading service info — don't show error yet
		if (sourceId && !serviceInfo) {
			return
		}

		// Cloud-mode service is loaded but has no endpoint configured —
		// surface that specifically rather than telling the user to "go back to
		// local sources" which is irrelevant for the spaces/rooms flow.
		if (sourceId && serviceInfo && !serviceInfo.endpoint) {
			setError(t('viewer.screen.serviceNotConfigured'))
			setIsLoading(false)
			return
		}

		setError(t('viewer.screen.missingSourceAddress'))
		setIsLoading(false)
		// `settings.format` is load-bearing in BOTH ad-hoc branches (it becomes
		// `source.format`, and it gates the HLS refusal above), so switching
		// format in the settings panel must re-run this. It was missing, which
		// meant a format switch on a `?url=` source left `source.format` stale
		// and the viewer kept rendering with the old element.
	}, [sourceId, urlParam, serviceInfo, serviceInfoError, liveKitSessionId, settings.format, t])

	// `useSourceFrame` (above) owns URL construction and the JPG poll timer.
	// Keep the local `isLoading` flag in sync once the hook has emitted a URL,
	// so the `<img>`/`<video>` element's `onLoad`/`onError` callbacks remain
	// the source of truth for *render*-side loading state. (The hook can't
	// observe network completion through an opaque media element.)
	useEffect(() => {
		if (imageUrl) {
			setIsLoading(false)
		}
	}, [imageUrl])

	// Nothing is decoded yet whenever the identity of what we're rendering
	// changes — a different source, or a format switch that swaps <img> for
	// <video>. Keyed on the cache-buster-free `baseUrl` on purpose: `imageUrl`
	// changes on every JPG poll tick, and re-arming the warm-up spinner once a
	// second would make a perfectly healthy snapshot source strobe.
	// The element-level failure goes with them: it described the PREVIOUS
	// source, and leaving it set opened a freshly-selected sibling already
	// showing the old camera's error card — the exact "this one's broken, try
	// the other" flow. `error` is deliberately left alone (see its docstring).
	// biome-ignore lint/correctness/useExhaustiveDependencies: change detector — the body only resets flags, the deps ARE the signal.
	useEffect(() => {
		setVideoReady(false)
		setImageReady(false)
		setLoadError(null)
		setFailedLoads(0)
		// The gesture requirement belonged to the previous source's element
		// state; a fresh source gets a fresh autoplay attempt first.
		setNeedsUserPlay(false)
	}, [sourceBaseUrl, settings.format])

	// HLS attachment — Chrome/Firefox/Edge can't decode `application/vnd.apple.mpegurl`
	// natively, so we wire up hls.js when the format is HLS. Safari has native
	// support and falls through to the regular `<video src>` path.
	//
	// hls.js is dynamically imported so the ~700 KB minified library stays out
	// of the main viewer bundle. It loads on-demand the first time a user
	// opens an HLS Source.
	useEffect(() => {
		// HLS and the relayed RTSP / NDI formats all arrive as an HLS manifest.
		if (!isHlsLikeFormat(settings.format)) return
		const video = videoRef.current
		if (!video || !imageUrl) return

		// WebKit (Safari and every iOS browser/WKWebView, including the bundled
		// mobile viewer) answers only `'maybe'` from `canPlayType` for HLS yet
		// decodes it natively in the `<video>` element via AVFoundation — while
		// iOS WebKit has no classic MediaSource, and hls.js's
		// ManagedMediaSource fallback stalls there with zero frames and no
		// fatal error (MP-175). Chromium-family engines also answer `'maybe'`
		// but genuinely cannot decode HLS natively (their no-cors `<video>`
		// fetches get ORB-blocked), so the relaxed gate must exclude real
		// Chromium. `Chrome/` (with slash) matches desktop/Android Chromium
		// but not iOS Chrome (`CriOS`), which is WebKit underneath and plays
		// HLS natively like Safari.
		const nativeAnswer = video.canPlayType('application/vnd.apple.mpegurl')
		const isNonChromiumWebKit =
			/AppleWebKit/.test(navigator.userAgent) &&
			!/Chrome\/|Chromium|Edg\/|OPR\/|SamsungBrowser|Android/.test(navigator.userAgent)
		const useNative = nativeAnswer === 'probably' || (nativeAnswer !== '' && isNonChromiumWebKit)

		let hls: { destroy: () => void } | null = null
		let cancelled = false
		let startHlsEngine: (() => void) | null = null

		// ------------------------------------------------------------------
		// Live-stream resume & self-heal (MU chyby 1+3, 2026-08-04).
		//
		// Backgrounding the tab (or the WKWebView in the mobile app) stops
		// segment polling; ~90s later the relay tears the on-demand path down
		// (HLS muxer 60s + runOnDemandCloseAfter 30s). On return both engines
		// used to die SILENTLY: AVFoundation keeps the last frame and gives no
		// JS signal at all, and hls.js's in-place recovery (startLoad) keeps
		// re-polling the torn-down session's variant playlist forever. The fix
		// is a full restart against a fresh MASTER fetch — the master URL is
		// session-less, so re-fetching it mints a new proxy session and
		// re-runs runOnDemand server-side (stream is back in ~3-6s).
		//
		// One bounded budget covers both engines; playback progress re-arms
		// it, so long sessions can survive any number of separate incidents
		// while a permanently-dead upstream still surfaces a real error.
		// ------------------------------------------------------------------
		let rebuilds = 0
		// Escalating delays before each restart. The first retry is fast (the
		// common case — returning from background just needs a fresh session),
		// the later ones are patient: after an ingest death the relay needs its
		// teardown window plus a cold start before a fresh master can succeed,
		// and immediate retries just burn the whole budget into the 502s
		// (observed 2026-08-04 00:29 during the kill-the-ingest live test).
		const REBUILD_DELAYS_MS = [1000, 4000, 8000, 15000]
		const MAX_REBUILDS = REBUILD_DELAYS_MS.length
		let rebuildTimer: number | null = null
		// Defeat any HTTP-level caching of the master playlist between
		// rebuilds; the proxy ignores unknown query params.
		const freshMasterUrl = () =>
			`${imageUrl}${imageUrl.includes('?') ? '&' : '?'}restart=${Date.now()}`
		// Guards the native error listener against the spurious error the
		// empty-src load() fires while we are swapping sources ourselves.
		let restarting = false

		const startNative = () => {
			restarting = true
			video.src = freshMasterUrl()
			video.load()
			// Explicit play(): the `autoPlay` ATTRIBUTE is only honoured on the
			// element's first load — WebKit does not re-trigger it after a
			// programmatic src swap + load(), so every native rebuild on iOS
			// left the fresh source PAUSED at frame zero. The stall watchdog
			// then read "no progress", rebuilt again, and the viewer looped
			// warm-up spinners forever without a single error event (the
			// mobile "HLS/MP4 never start" report, 2026-08-14). A refusal
			// (autoplay policy) raises the tap-to-play overlay.
			playVideo(video)
			// The empty-src / source-swap error task (if any) is queued before
			// this timeout fires; real network errors on the new source arrive
			// later and are honoured.
			window.setTimeout(() => {
				restarting = false
			}, 0)
		}

		const rebuild = () => {
			if (cancelled || rebuildTimer !== null) return
			if (rebuilds >= MAX_REBUILDS) {
				hls?.destroy()
				hls = null
				reportLoadFailure('viewer.screen.cannotPlayVideo')
				return
			}
			const delay = REBUILD_DELAYS_MS[rebuilds]
			rebuilds += 1
			rebuildTimer = window.setTimeout(() => {
				rebuildTimer = null
				if (cancelled) return
				// A fresh engine starts at currentTime 0 — leaving the previous
				// engine's high-water mark in place would make the watchdog read
				// every recovered stream as stalled (currentTime never exceeds
				// the old mark before the next teardown) and kill healthy
				// engines in a loop, which is exactly what the 00:38 live test
				// showed. Reset the progress baseline with the engine.
				lastTime = -1
				stalledTicks = 0
				if (useNative) {
					startNative()
				} else {
					hls?.destroy()
					hls = null
					startHlsEngine?.()
				}
			}, delay)
		}

		// Stall watchdog: a live stream must advance currentTime. No progress
		// for two ticks (~8s) while visible and not user-paused means the
		// upstream is gone — rebuild. Progress resets the rebuild budget.
		let lastTime = -1
		let stalledTicks = 0
		const WATCHDOG_MS = 4000
		const STALL_TICKS = 2
		const watchdog = window.setInterval(() => {
			if (cancelled || document.hidden || video.paused || rebuildTimer !== null) return
			if (video.currentTime > lastTime) {
				lastTime = video.currentTime
				stalledTicks = 0
				rebuilds = 0
				return
			}
			stalledTicks += 1
			if (stalledTicks >= STALL_TICKS) {
				stalledTicks = 0
				rebuild()
			}
		}, WATCHDOG_MS)

		// Fast path for the background→foreground return: don't make the user
		// stare at a frozen frame for a watchdog cycle — if playback didn't
		// advance while hidden, restart immediately.
		const onVisibilityChange = () => {
			if (document.hidden || cancelled || rebuildTimer !== null) return
			if (!video.paused && video.currentTime <= lastTime) {
				stalledTicks = 0
				rebuild()
			}
		}
		document.addEventListener('visibilitychange', onVisibilityChange)

		const onNativeError = () => {
			if (restarting || cancelled) return
			rebuild()
		}

		if (useNative) {
			// WebKit decodes HLS natively via AVFoundation (see the gate
			// above) — but it reports upstream death only through the media
			// element's error event, if at all. Route it into the shared
			// rebuild budget.
			video.addEventListener('error', onNativeError)
			startNative()
		} else {
			void import('hls.js').then(({ default: Hls }) => {
				if (cancelled) return
				if (!Hls.isSupported()) {
					reportLoadFailure('viewer.screen.cannotPlayVideo')
					return
				}
				startHlsEngine = () => {
					// Per-instance in-place recovery counters; a rebuilt engine
					// gets a fresh budget (rebuilds themselves are bounded).
					let networkRecoveries = 0
					let mediaRecoveries = 0
					const MAX_NETWORK_RECOVERIES = 3
					const MAX_MEDIA_RECOVERIES = 2
					const instance = new Hls({
						// Sliding-window live streams: keep the back-buffer trimmed so
						// the browser doesn't accumulate memory across long sessions.
						backBufferLength: 30,
						// hls.js retries the manifest on transient errors; pin it to a
						// short interval so a momentarily-unhealthy upstream recovers
						// quickly rather than leaving the viewer stuck for a minute.
						manifestLoadingMaxRetry: 4,
						manifestLoadingRetryDelay: 500,
						// A non-PUBLIC HLS source authorizes via the injected session token
						// (M1). <video>/hls.js can't carry a header on the element itself, so
						// attach the bearer to the manifest + segment XHRs here — but ONLY for
						// our own API origin. An HLS manifest can reference absolute,
						// cross-origin segment URLs (multi-CDN, or a malicious/compromised
						// upstream an admin controls); without this origin gate hls.js would
						// send the token on those direct fetches and leak it to an untrusted
						// host. Proxied same-origin segments still authorize. No-op outside a
						// native WebView (no token / no injected apiUrl).
						xhrSetup: (xhr: XMLHttpRequest, url: string) => {
							const nativeToken = getNativeToken()
							const apiUrl = getNativeApiUrl()
							if (nativeToken && apiUrl && isSameApiOrigin(url, apiUrl)) {
								xhr.setRequestHeader('Authorization', `Bearer ${nativeToken}`)
							}
						},
					})
					instance.loadSource(freshMasterUrl())
					instance.attachMedia(video)
					instance.on(Hls.Events.ERROR, (_evt, data) => {
						if (!data.fatal) return
						// In-place recovery first — the canonical hls.js pattern:
						// NETWORK → startLoad(); MEDIA → recoverMediaError(). When
						// that budget is spent the session itself is likely dead
						// (torn-down on-demand path), so fall through to a full
						// fresh-master rebuild instead of giving up outright.
						switch (data.type) {
							case Hls.ErrorTypes.NETWORK_ERROR:
								if (networkRecoveries < MAX_NETWORK_RECOVERIES) {
									networkRecoveries += 1
									instance.startLoad()
									return
								}
								break
							case Hls.ErrorTypes.MEDIA_ERROR:
								if (mediaRecoveries < MAX_MEDIA_RECOVERIES) {
									mediaRecoveries += 1
									instance.recoverMediaError()
									return
								}
								break
						}
						rebuild()
					})
					hls = instance
				}
				startHlsEngine()
			})
		}

		return () => {
			cancelled = true
			window.clearInterval(watchdog)
			if (rebuildTimer !== null) window.clearTimeout(rebuildTimer)
			document.removeEventListener('visibilitychange', onVisibilityChange)
			if (useNative) {
				video.removeEventListener('error', onNativeError)
				// Stop the live stream's segment fetches on unmount/source
				// switch — mirrors the hls.js path's destroy().
				video.removeAttribute('src')
				video.load()
			}
			hls?.destroy()
		}
	}, [settings.format, imageUrl, reportLoadFailure, playVideo])

	// Fullscreen handling.
	//
	// iOS is the whole reason this is not just the Fullscreen API: WebKit
	// exposes `requestFullscreen` on video elements only, so on an iPhone or
	// iPad the button called a method that does not exist and nothing at all
	// happened (MU, 17. 9. 2026: "tlačítko full-screen nefunguje"). Immersive
	// mode is what actually makes the picture fill the screen — it hides the
	// viewer's own chrome and tells the native shell to drop its header — and
	// the Fullscreen API is a bonus on top for browsers that have it. This
	// mirrors the transcript viewer, which has worked this way since MU asked
	// for tap-to-fullscreen.
	const toggleFullscreen = useCallback(() => {
		setShowSettings(false)
		// Drive the API from the mode being ENTERED, not from
		// `document.fullscreenElement`. The two can disagree: a rejected
		// request correctly leaves immersive on while fullscreen is off, and
		// reading the document state would then make the next press — a
		// request to LEAVE — re-request fullscreen instead.
		const next = !immersive
		setImmersive(next)
		if (isNativeWebView() || !containerRef.current) return
		// Needs a user gesture and a permissive iframe policy, and rejects
		// otherwise. A rejection must not undo the mode the user just asked for.
		try {
			if (next) {
				containerRef.current.requestFullscreen?.().catch(console.warn)
			} else if (document.fullscreenElement) {
				document.exitFullscreen?.().catch(console.warn)
			}
		} catch (err) {
			console.warn('[ScreenViewer] fullscreen request refused:', err)
		}
	}, [immersive])

	useEffect(() => {
		const handleFullscreenChange = () => {
			const active = !!document.fullscreenElement
			setIsFullscreen(active)
			// Leaving fullscreen through the browser's own Escape must bring the
			// controls back too, or the button reads "exit" with nothing to exit.
			if (!active) setImmersive(false)
		}
		document.addEventListener('fullscreenchange', handleFullscreenChange)
		return () => document.removeEventListener('fullscreenchange', handleFullscreenChange)
	}, [])

	// Keyboard shortcuts
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return
			// Space activates a focused button/link/select natively. This handler
			// preventDefaults it, so bail out on any interactive target — otherwise
			// the toolbar and the settings panel stop responding to the keyboard,
			// which is the input method this viewer's users rely on most.
			if (e.target instanceof HTMLElement && e.target.closest('button, a, select, [role="button"]'))
				return

			switch (e.key.toLowerCase()) {
				case 'f':
					toggleFullscreen()
					break
				case 'escape':
					if (isFullscreen) {
						// `?.` for parity with the toggle above: WebKit exposes
						// this on video elements only, so on iOS the call would
						// throw a TypeError and take the whole handler with it.
						document.exitFullscreen?.().catch(console.warn)
					} else if (immersive) {
						setImmersive(false)
					} else {
						setShowSettings(false)
					}
					break
				case ' ':
					// Same gate as the toolbar button: without it Space could pause an
					// MP4/HLS lecture from a control the viewer can't see. Swallow the
					// key either way so it can't scroll the landscape settings overlay
					// — focused controls already returned above.
					e.preventDefault()
					if (!canPause) break
					setIsPlaying((prev) => !prev)
					break
				case '+':
				case '=':
					if (!settings.zoomLocked) {
						setSettings((prev) => ({
							...prev,
							zoomLevel: clampZoom(prev.zoomLevel + 0.1),
						}))
					}
					break
				case '-':
					if (!settings.zoomLocked) {
						setSettings((prev) => ({
							...prev,
							zoomLevel: clampZoom(prev.zoomLevel - 0.1),
						}))
					}
					break
				case 'g':
					setSettings((prev) => ({ ...prev, grayscale: !prev.grayscale }))
					break
				case 'i':
					setSettings((prev) => ({ ...prev, inverted: !prev.inverted }))
					break
			}
		}

		window.addEventListener('keydown', handleKeyDown)
		return () => window.removeEventListener('keydown', handleKeyDown)
	}, [toggleFullscreen, isFullscreen, immersive, settings.zoomLocked, canPause])

	const handleZoom = useCallback(
		(delta: number) => {
			if (settings.zoomLocked) return
			setSettings((prev) => ({
				...prev,
				zoomLevel: clampZoom(prev.zoomLevel + delta),
			}))
		},
		[settings.zoomLocked],
	)

	// No `preventDefault()` here or in the touch handlers: React registers
	// onWheel/onTouch* passively at the root, so the call is a no-op that logs
	// a console warning every frame — noise in a WebView where the console is
	// the only diagnostic channel. Gesture suppression is done by `touch-none`
	// on the pane, and the h-screen/overflow-hidden layout leaves the wheel
	// nothing to scroll.
	const handleWheel = (e: React.WheelEvent) => {
		if (settings.zoomLocked) return
		const delta = e.deltaY > 0 ? -0.1 : 0.1
		handleZoom(delta)
	}

	// Detect the viewport via JS (more reliable than CSS in WebViews). Both the
	// orientation and the shorter side matter, so track the size itself rather
	// than a single boolean.
	const [viewport, setViewport] = useState(() =>
		typeof window === 'undefined'
			? { width: 0, height: 0 }
			: { width: window.innerWidth, height: window.innerHeight },
	)

	useEffect(() => {
		const read = () => setViewport({ width: window.innerWidth, height: window.innerHeight })
		const mql = window.matchMedia('(orientation: landscape)')
		mql.addEventListener('change', read)
		// Also listen to resize as a fallback for WebViews
		window.addEventListener('resize', read)
		read()
		return () => {
			mql.removeEventListener('change', read)
			window.removeEventListener('resize', read)
		}
	}, [])

	const isLandscape = viewport.width > viewport.height

	/** Pan offset of the zoomed picture, in unscaled pane pixels. */
	const [pan, setPan] = useState({ x: 0, y: 0 })
	/**
	 * `pan` for the arrow-key listener, which must not re-subscribe on every
	 * press. The listener also writes it, so a held key's auto-repeat that
	 * lands before the re-render steps from the new offset, not the stale one.
	 */
	const panRef = useRef(pan)
	panRef.current = pan
	/** The clipping pane the picture is panned inside. */
	const paneRef = useRef<HTMLElement>(null)
	/** In-flight one-finger / mouse drag, in unscaled pane pixels. */
	const dragRef = useRef<{
		startX: number
		startY: number
		originX: number
		originY: number
	} | null>(null)

	/**
	 * Drag-to-pan above 100 %.
	 *
	 * Zoom is a `scale()` on a pane-sized wrapper inside an `overflow-hidden`
	 * section, so everything past the pane's edges is simply clipped: zooming
	 * in showed the middle of the picture and there was no way to reach the
	 * rest (MU, 17. 9. 2026: "při zvětšení > 100 % nelze posouvat"). A
	 * translation on the same wrapper moves which part of the enlarged picture
	 * the pane shows.
	 */
	const clampPan = useCallback((offset: { x: number; y: number }, zoom: number) => {
		const rect = paneRef.current?.getBoundingClientRect()
		return rect ? clampPanOffset(offset, zoom, rect) : offset
	}, [])

	const beginPan = useCallback(
		(clientX: number, clientY: number) => {
			if (settings.zoomLevel <= 1) return
			dragRef.current = { startX: clientX, startY: clientY, originX: pan.x, originY: pan.y }
		},
		[settings.zoomLevel, pan.x, pan.y],
	)

	const continuePan = useCallback(
		(clientX: number, clientY: number) => {
			const drag = dragRef.current
			if (!drag) return
			// 1:1 with the finger. Whether the press was a pan or a tap is the
			// double-tap tracker's call (useImmersiveDoubleTap), not this one's.
			const dx = clientX - drag.startX
			const dy = clientY - drag.startY
			setPan(clampPan({ x: drag.originX + dx, y: drag.originY + dy }, settings.zoomLevel))
		},
		[clampPan, settings.zoomLevel],
	)

	const handleMouseDown = useCallback(
		(e: React.MouseEvent) => {
			// Primary button only; the context menu and middle-click paste have
			// their own jobs.
			if (e.button !== 0) return
			beginPan(e.clientX, e.clientY)
		},
		[beginPan],
	)

	const handleMouseMove = useCallback(
		(e: React.MouseEvent) => {
			if (!dragRef.current) return
			e.preventDefault()
			continuePan(e.clientX, e.clientY)
		},
		[continuePan],
	)

	const handleMouseUp = useCallback(() => {
		dragRef.current = null
	}, [])

	// Zooming back out to fit leaves no room to pan, so a stale offset would
	// show as an off-centre picture that cannot be corrected.
	// Re-clamp whenever the PANE changes size, not just the window. Rotation,
	// immersive mode and fullscreen all move `window.inner*`, but opening the
	// settings panel inserts it above the pane and shrinks it with no resize
	// event at all — and a pan that was legal at the old size then pulls the
	// picture off the opposite edge until the next drag. Observing the box
	// itself covers every cause, the way the transcript viewer already does.
	useEffect(() => {
		const el = paneRef.current
		if (!el) return
		const reclamp = () => {
			setPan((prev) => {
				if (settings.zoomLevel <= 1) return prev.x === 0 && prev.y === 0 ? prev : { x: 0, y: 0 }
				const next = clampPan(prev, settings.zoomLevel)
				return next.x === prev.x && next.y === prev.y ? prev : next
			})
		}
		reclamp()
		const observer = new ResizeObserver(reclamp)
		observer.observe(el)
		return () => observer.disconnect()
	}, [settings.zoomLevel, clampPan])

	/**
	 * Arrow keys pan the zoomed picture a tenth of the pane per press, clamped
	 * like a drag (MU, 17. 9. 2026: "při zvětšení obrazu víc než 100 % nelze
	 * posouvat zobrazení ←↑↓→"). Page-wide, like the other shortcuts, and
	 * deliberately not behind their focused-button bail-out: focus is still on
	 * the zoom button the reader just pressed. At or below 100 % and in fields
	 * that use arrows themselves the key is left alone — see
	 * keyboardPanForKeyDown.
	 */
	useEffect(() => {
		const handleArrowPan = (e: KeyboardEvent) => {
			if (e.defaultPrevented) return
			const rect = paneRef.current?.getBoundingClientRect()
			if (!rect) return
			const target =
				e.target instanceof HTMLElement
					? { tagName: e.target.tagName, isContentEditable: e.target.isContentEditable }
					: null
			const next = keyboardPanForKeyDown(
				{ key: e.key, altKey: e.altKey, ctrlKey: e.ctrlKey, metaKey: e.metaKey, target },
				{ zoom: settings.zoomLevel, pan: panRef.current },
				rect,
			)
			if (!next) return
			// The arrow is ours: it must not also scroll the page.
			e.preventDefault()
			panRef.current = next
			setPan(next)
		}
		window.addEventListener('keydown', handleArrowPan)
		return () => window.removeEventListener('keydown', handleArrowPan)
	}, [settings.zoomLevel])

	/**
	 * Two-finger gesture: zoom by the spread, pan by the midpoint.
	 *
	 * The pinch used to scale about the pane centre and ignore where the
	 * fingers went, and lifting one finger ended the gesture, so the remaining
	 * finger did not pan either. Panning "moved terribly little" (MU,
	 * 25. 9. 2026). Now the spot between the fingers stays under them — see
	 * pinchPanOffset — and a finger left on the glass carries on panning.
	 */
	const pinchRef = useRef<(PinchStart & { distance: number }) | null>(null)

	/** A touch point relative to the pane centre, the origin `pan` is measured from. */
	const fromPaneCentre = useCallback((x: number, y: number) => {
		const rect = paneRef.current?.getBoundingClientRect()
		if (!rect) return null
		return { x: x - (rect.left + rect.width / 2), y: y - (rect.top + rect.height / 2) }
	}, [])

	const handleTouchStart = useCallback(
		(e: React.TouchEvent) => {
			// One finger pans the enlarged picture; two pinch and pan it. A pinch
			// that starts as one finger hands over here.
			if (e.touches.length === 1) {
				beginPan(e.touches[0].clientX, e.touches[0].clientY)
				return
			}
			if (e.touches.length !== 2) return
			dragRef.current = null
			const [a, b] = [e.touches[0], e.touches[1]]
			const mid = fromPaneCentre((a.clientX + b.clientX) / 2, (a.clientY + b.clientY) / 2)
			if (!mid) return
			pinchRef.current = {
				distance: Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY),
				zoom: settings.zoomLevel,
				pan,
				mid,
			}
		},
		[settings.zoomLevel, pan, beginPan, fromPaneCentre],
	)

	const handleTouchMove = useCallback(
		(e: React.TouchEvent) => {
			if (dragRef.current && e.touches.length === 1) {
				continuePan(e.touches[0].clientX, e.touches[0].clientY)
				return
			}
			const pinch = pinchRef.current
			if (!pinch || e.touches.length !== 2) return
			const [a, b] = [e.touches[0], e.touches[1]]
			const mid = fromPaneCentre((a.clientX + b.clientX) / 2, (a.clientY + b.clientY) / 2)
			if (!mid) return
			// A locked zoom still pans with two fingers; it just does not scale.
			const spread = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY)
			const zoom =
				settings.zoomLocked || pinch.distance === 0
					? pinch.zoom
					: clampZoom((pinch.zoom * spread) / pinch.distance)
			setSettings((prev) => (prev.zoomLevel === zoom ? prev : { ...prev, zoomLevel: zoom }))
			setPan(clampPan(pinchPanOffset(pinch, mid, zoom), zoom))
		},
		[continuePan, clampPan, fromPaneCentre, settings.zoomLocked],
	)

	const handleTouchEnd = useCallback(
		(e: React.TouchEvent) => {
			pinchRef.current = null
			dragRef.current = null
			// One finger lifted from a pinch: the other one keeps panning from
			// wherever the pinch left the picture.
			if (e.touches.length === 1) beginPan(e.touches[0].clientX, e.touches[0].clientY)
		},
		[beginPan],
	)

	const getFormatIcon = (format: ScreenFormat) => {
		switch (format) {
			case ScreenFormat.JPG:
				return <Image size={16} />
			case ScreenFormat.MJPG:
			case ScreenFormat.MP4:
			case ScreenFormat.HLS:
			case ScreenFormat.RTSP:
			case ScreenFormat.NDI:
				return <Video size={16} />
			default:
				return <Monitor size={16} />
		}
	}

	const renderContent = () => {
		const filterStyle =
			`${settings.grayscale ? 'grayscale(100%)' : ''} ${settings.inverted ? 'invert(100%)' : ''}`.trim()

		// `translate` before `scale` so the pan offset stays in the pane's own
		// pixels: transform functions apply right to left, so the translation
		// is not multiplied by the zoom and a finger travels with the picture.
		const zoomPaneStyle: CSSProperties = {
			transform: `translate(${pan.x}px, ${pan.y}px) scale(${settings.zoomLevel})`,
			transformOrigin: 'center center',
			cursor: settings.zoomLevel > 1 ? 'grab' : undefined,
		}

		// LiveKit-backed source takes precedence over the format-keyed JPEG/video
		// renderers: a room "Present" publishes a WebRTC screen track to a live
		// session, so when `serviceInfo` reports a `liveSessionId` we subscribe to
		// that instead of polling a (nonexistent) static endpoint. Same zoom
		// container + a11y `filterStyle` as the other renderers — zoom / grayscale
		// / invert all keep working on the live video.
		if (liveKitSessionId) {
			return (
				<div
					className="h-full w-full flex items-center justify-center overflow-hidden"
					style={zoomPaneStyle}
				>
					{/* Keyed by session id: when the presenter restarts a broadcast the
					    service can flip to a NEW LiveSession (new LiveKit room). A
					    remount tears the old room connection down cleanly instead of
					    relying on LiveKitRoom to swap tokens in place. */}
					<LiveKitScreenPane
						key={liveKitSessionId}
						sessionId={liveKitSessionId}
						filterStyle={filterStyle}
						onPresenterName={setPresenterName}
						onConnectionChange={setLiveKitConnected}
						// Fatal room errors (connect failure, signal loss…) escalate to
						// the page-level error state so the viewer gets the full-area
						// error UI with back/retry — not an infinite "waiting" pane.
						onFatalError={(message) =>
							setError(t('viewer.screen.liveConnectionFailed', { message }))
						}
					/>
				</div>
			)
		}

		switch (settings.format) {
			case ScreenFormat.JPG:
			case ScreenFormat.MJPG:
				return imageUrl ? (
					<div
						className="relative h-full w-full flex items-center justify-center overflow-hidden"
						style={zoomPaneStyle}
					>
						<img
							src={imageSrc ?? undefined}
							alt={t('viewer.screen.presentationImage')}
							// pointer-events:none keeps the viewer "watch only" — no click-to-
							// drag, no native right-click save dialog. Toolbar buttons and
							// keyboard shortcuts remain the only ways to alter view state.
							// `w-full h-full object-contain` (NOT max-w/max-h) so a low-res
							// capture fills the viewport + letterboxes instead of rendering at
							// its intrinsic size in a larger box — matches the live-sessions
							// pane so both entry points frame the presentation identically
							// (MP-198). Zoom still rides the outer container's scale().
							// `invisible` (not conditional rendering) whenever the element
							// holds no decoded frame: it has to stay mounted for the browser
							// to fetch it at all, but an <img> with nothing in it paints the
							// broken-image glyph + alt text — which is what MU reported as
							// "rozbitá ikona uprostřed plochy".
							className={cn(
								'w-full h-full object-contain pointer-events-none select-none',
								!imageReady && 'invisible',
							)}
							draggable={false}
							style={{ filter: filterStyle }}
							onError={() => {
								// A failed load does NOT leave the previous frame on screen —
								// verified in Chrome: assigning a src that 502s clears the
								// element and paints the glyph. So "we had a frame" stops
								// being true the moment one fetch fails, and the pane hands
								// over to the full-area error state instead of showing a
								// broken picture. Self-healing: the next successful poll
								// fires onLoad, which re-arms this and clears the error.
								setImageReady(false)
								reportLoadFailure('viewer.screen.cannotLoadImage')
							}}
							onLoad={() => {
								invalidateProbe()
								frameFailureReportedRef.current = false
								setIsLoading(false)
								setImageReady(true)
								setLoadError(null)
								setFailedLoads(0)
							}}
						/>
						{!imageReady && !errorOwnsPane && (
							// Warm-up overlay, mirroring the video path: a snapshot proxied
							// through the server (cold relay, slow camera) can take seconds
							// before the first frame lands, and silence reads as a hang.
							<div className="absolute inset-0 flex items-center justify-center pointer-events-none">
								<span className="text-primary">
									<Spinner size="lg" />
								</span>
							</div>
						)}
					</div>
				) : (
					<div className="h-full flex items-center justify-center">
						<span className="text-primary">
							<Spinner size="lg" />
						</span>
					</div>
				)

			case ScreenFormat.MP4:
			case ScreenFormat.HLS:
			case ScreenFormat.RTSP:
			case ScreenFormat.NDI: {
				// HLS-like path (HLS / RTSP / NDI): the useEffect above attaches via
				// hls.js (or sets `video.src` directly on Safari). Don't set `src` on
				// the `<video>` element here for those — that would race with hls.js.
				// MP4 is the one video format that keeps a native `src`.
				const isHls = isHlsLikeFormat(settings.format)
				return (
					<div
						className="relative h-full w-full flex items-center justify-center overflow-hidden"
						style={zoomPaneStyle}
					>
						<video
							ref={videoRef}
							{...(isHls ? {} : { src: imageUrl ?? '' })}
							// `controls` removed + pointer-events:none — the viewer is for
							// hearing-/vision-impaired students; clicking the canvas must
							// never pause the lecture. Toolbar buttons and keyboard
							// shortcuts are the operator-grade controls.
							// `w-full h-full object-contain` so the video fills + letterboxes
							// like the live-sessions pane rather than capping at intrinsic
							// size (MP-198). Zoom rides the outer container's scale().
							className="w-full h-full object-contain pointer-events-none select-none"
							style={{ filter: filterStyle }}
							autoPlay={isPlaying}
							muted
							loop
							playsInline
							disablePictureInPicture
							disableRemotePlayback
							onError={() => reportLoadFailure('viewer.screen.cannotPlayVideo')}
							onLoadedData={(e) => {
								invalidateProbe()
								setIsLoading(false)
								setVideoReady(true)
								setLoadError(null)
								setFailedLoads(0)
								// Resume after engine rebuilds: a swapped src (native
								// restart, fresh hls.js MediaSource) loads PAUSED because
								// the autoPlay attribute is only honoured on the first
								// load. Without this the stall watchdog reads the paused
								// element as "no progress" and rebuild-loops forever.
								const media = e.currentTarget
								if (isPlayingRef.current && media.paused) {
									playVideo(media)
								}
							}}
							onPlay={() => setNeedsUserPlay(false)}
						>
							{t('viewer.screen.browserNotSupported')}
						</video>
						{needsUserPlay && !errorOwnsPane && (
							// Autoplay was refused and only a real user gesture may start
							// playback. The video element itself is pointer-events-none
							// (a stray tap must never pause a lecture), which also makes
							// WebKit's built-in play glyph untappable — this overlay is
							// the tappable stand-in, and its click carries the gesture.
							<button
								type="button"
								onClick={() => {
									const video = videoRef.current
									if (!video) return
									setIsPlaying(true)
									playVideo(video)
								}}
								className="absolute inset-0 flex items-center justify-center bg-black/30"
								aria-label={t('viewer.screen.tapToPlay')}
							>
								<span className="flex flex-col items-center gap-3">
									<span className="rounded-full bg-base-100/90 p-6 text-primary shadow-lg">
										<Play size={44} />
									</span>
									<span className="text-white text-sm font-medium drop-shadow">
										{t('viewer.screen.tapToPlay')}
									</span>
								</span>
							</button>
						)}
						{!videoReady && !errorOwnsPane && !needsUserPlay && (
							// Warm-up overlay: for on-demand RTSP/NDI the relay needs a few
							// seconds to pull + transcode the first segment, during which the
							// video is legitimately empty. Show a spinner instead of a blank
							// pane so it doesn't read as broken (hls.js self-heals — it keeps
							// retrying and starts decoding once the source is warm).
							<div className="absolute inset-0 flex items-center justify-center pointer-events-none">
								<span className="text-primary">
									<Spinner size="lg" />
								</span>
							</div>
						)}
					</div>
				)
			}

			default:
				return (
					<div className="h-full flex items-center justify-center">
						<span className="text-primary">
							<Spinner size="lg" />
						</span>
					</div>
				)
		}
	}

	// Is there anything for the viewer to look at right now? Only one of the two
	// elements is ever mounted for a given format, and both flags reset together
	// when the source changes, so the union is exact rather than a heuristic.
	// This decides where a failure is reported: over the whole pane when the
	// pane is empty, or as a header badge next to a last-good frame.
	// A polled frame that failed in the bearer path stays on screen with a header
	// badge, as nothing cleared the element. After the usual run of failures it is
	// no longer the picture, so it must stop passing for one.
	const framePollFrameIsStale = framePoll && failedLoads >= FAILED_LOADS_BEFORE_TAKEOVER
	const hasDecodedFrame = (imageReady && !framePollFrameIsStale) || videoReady

	// Whichever failure is current; page-level wins because it explains more.
	const activeError = error ?? loadError

	// Does a failure take over the whole pane, or stay a header badge?
	//
	// A polled source re-requests every `refreshInterval` (1 s by default), so
	// ONE failed tick is the normal shape of a flaky camera, not news. Promoting
	// it straight to the full-area card made the viewport alternate between
	// picture and error card at up to 1 Hz on an intermittent source — unusable
	// in a viewer built for vision-impaired students, and the warm-up second of
	// a cold source flashed a full-screen error before the first frame. Wait for
	// several consecutive failures instead; the spinner covers the interval.
	//
	// A streaming source (MJPG, MP4, HLS) gets one error event and never retries
	// by itself, so for those the first failure is already terminal and waiting
	// would just mean staring at a spinner. Page-level errors never wait.
	const errorOwnsPane =
		!hasDecodedFrame &&
		(!!error ||
			(!!loadError &&
				(!(isPolledSourceFormat(settings.format) || framePoll) ||
					// Polling is waiting out a 429, so no further failure will come
					// along to raise the count, and the reason must not sit behind a
					// spinner.
					!!pollThrottle ||
					failedLoads >= FAILED_LOADS_BEFORE_TAKEOVER)))

	const isNative = isNativeWebView()
	// The phone's text size above the default (iOS hands it over the bridge).
	const isTextEnlarged = (getNativeFontScale() ?? 1) > 1
	const isStandaloneViewer = useIsStandaloneViewer()

	/**
	 * Native landscape swaps the header for a floating settings button (and the
	 * shell’s back chip) so the picture gets the
	 * whole screen. That trade only pays on a phone: on an iPad it took the
	 * controls off a display with room to spare, which is what MU reported on
	 * 17. 9. 2026 ("iPad na šířku zmizí ovládací prvky"). A tablet in landscape
	 * keeps the ordinary header.
	 */
	const isNativeLandscape =
		isNative && isLandscape && Math.min(viewport.width, viewport.height) < TABLET_MIN_SHORT_SIDE_PX

	/**
	 * Double tap on the picture leaves immersive mode, the same gesture as the
	 * transcript viewers; it never enters it — the header button and F do (MU
	 * 25. 9. 2026). A single tap toggled before, and a pinch or a pan sometimes
	 * "caught it" ("při zvětšování/zmenšování se to někdy chytne"): any press
	 * with a second finger or a travelling finger is now not a tap at all.
	 */
	const exitImmersiveFromTap = useCallback(() => {
		setShowSettings(false)
		setImmersive(false)
		exitBrowserFullscreen()
	}, [])
	const paneTap = useImmersiveDoubleTap(immersive, exitImmersiveFromTap)

	// The mobile app draws its own native header above this WebView. Tell it to
	// hide that header too, otherwise one bar stays behind and the mode looks
	// broken. Same contract as the transcript viewer.
	useEffect(() => {
		if (!isNative) return
		try {
			const bridge = (
				window as unknown as {
					ReactNativeWebView?: { postMessage: (s: string) => void }
				}
			).ReactNativeWebView
			if (!bridge) {
				// `isNative` said we are inside the app, so the bridge should
				// exist. Staying silent here produces exactly the split state
				// the comment above warns about: web chrome hidden, native
				// header still showing.
				console.warn('[ScreenViewer] native shell detected but no WebView bridge')
				return
			}
			bridge.postMessage(JSON.stringify({ source: 'screen-viewer', type: 'immersive', immersive }))
		} catch (err) {
			// Plain browser, or the payload failed to stringify. Immersive mode
			// still works here; only the native header lags.
			console.warn('[ScreenViewer] immersive bridge failed:', err)
		}
	}, [isNative, immersive])

	// UNAUTHORIZED from `getService` → the service exists but wants a signed-in
	// viewer. Offer a login CTA that round-trips back to this exact viewer URL
	// via the same `/auth?returnTo=` mechanism AuthGuard uses (the auth page
	// validates it through `getSafeReturnTo` — same-origin paths only).
	//
	// Suppressed in the prebundled viewer, which has no `/auth` route: a tap
	// would match no route, render nothing, and leave a blank WebView — the
	// same failure #647 reasoned about for the room link, which is the sibling
	// button in this very overlay. Reachable when the shell is signed out (no
	// bridge token) and hands the viewer a serviceId for a non-PUBLIC service,
	// e.g. from a QR scan.
	// Gated on the router, not on `isNative`: the mobile session WebView loads
	// polygraf.app itself, where `/auth` resolves and this CTA is the only way
	// an anonymous viewer can sign in.
	const serviceErrorCode = (serviceInfoError?.data as { code?: string } | null | undefined)?.code
	const loginHref =
		serviceErrorCode === 'UNAUTHORIZED' && !isStandaloneViewer
			? `/auth?returnTo=${encodeURIComponent(location.pathname + location.search)}`
			: undefined

	return (
		<div
			ref={containerRef}
			// Fill the full viewport height. ScreenViewerPage always renders in a
			// chrome-less wrapper (app.tsx viewer branch + screen-viewer-entry), so
			// the flex column owns the whole screen: header + flex-1 media area +
			// the control bar, which lands flush at the bottom. The old
			// `max-h-[calc(100vh-12rem)]` was a leftover from when this sat inside
			// app chrome — it left dead space below and floated the control bar
			// mid-screen. `h-screen` matches the sibling viewers (unified/transcript).
			// The ground itself is `viewer-surface` (theme-aware white / #0d1117),
			// overridden by `surface()` when the transcript viewer has colours to
			// follow. It replaced a hardcoded `bg-white`, which in turn replaced
			// `bg-base-100` — that resolves through the `polygraf` daisyUI theme to
			// cream #faf8f5, so shipping it left a visible tint and MU reported on
			// 4. 9. that nothing had changed.
			{...surface(
				`relative flex ${isNativeLandscape ? 'flex-row' : 'flex-col'} ${isFullscreen ? 'fixed inset-0 z-50 p-2' : 'h-screen'}`,
			)}
		>
			<IphoneFullscreenHint active={immersive} />
			{/* Native landscape drops the header to give the picture the whole
			    screen; the settings toggle stays reachable as a small overlay,
			    otherwise filters and zoom would have no entry point there. */}
			{isNativeLandscape && !immersive && (
				<button
					type="button"
					className={`absolute top-2 right-2 z-30 p-2.5 rounded-lg${followedColors ? '' : ' viewer-surface-80'} transition-colors ${showSettings ? 'bg-primary/20' : 'hover:bg-black/10'}`}
					// `viewer-surface-80` is translucent (and theme-aware, so it is
					// not simply white): a followed ground would show through it at
					// an unknown effective contrast. Follow opaquely instead.
					style={
						followedColors
							? {
									backgroundColor: followedColors.backgroundColor,
									color: followedColors.textColor,
								}
							: undefined
					}
					onClick={() => setShowSettings(!showSettings)}
					title={t('viewer.screen.settings.title')}
					aria-label={t('viewer.screen.settings.title')}
					aria-expanded={showSettings}
				>
					<Settings size={20} />
				</button>
			)}
			{/* Header — hidden in native landscape and in immersive mode */}
			{!isNativeLandscape && !immersive && (
				<header className="flex flex-wrap sm:flex-nowrap items-center justify-between px-3 py-2 gap-2 border-b border-base-300">
					{/* Below `sm` the source picker wraps onto its own full-width row: sharing
					    one row with the title squeezed the title to a few letters on a phone
					    ("další… / Testo…", Natálie's retest 24. 9. 2026).
					    The title column wraps rather than truncates (MU, 7. 10. 2026): with
					    the phone's largest text size the name, room and status each run
					    onto more lines, and once the column would drop under ~8 em the
					    buttons move to a row of their own. At the default size the basis
					    is 128 px, so a phone keeps one row — as in the transcript viewer. */}
					<div className="flex grow basis-[calc(8rem*var(--text-scale,1))] items-center gap-2 min-w-0">
						{!isFullscreen && !isNative && (
							<Link
								to={
									serviceInfo ? viewerRoomPath(serviceInfo.room.spaceId, serviceInfo.room.id) : '/'
								}
								className={cn(
									buttonClasses({ variant: 'ghost', size: 'sm' }),
									'p-2 rounded-full inline-flex items-center justify-center',
								)}
								aria-label={t('viewer.screen.backToRoom')}
							>
								<ArrowLeft size={18} />
							</Link>
						)}
						<div className="min-w-0">
							<h1 className="text-base font-semibold break-words hyphens-auto">
								{serviceInfo?.name ?? source?.name ?? t('viewer.screen.title')}
							</h1>
							{serviceInfo?.room && (
								<ViewerBreadcrumb room={serviceInfo.room} linked={!isNative} wrap />
							)}
							{/* MP-187: show who is presenting (LiveKit-backed broadcasts). */}
							{liveKitSessionId && presenterName && (
								<div className="text-sm opacity-70 break-words hyphens-auto">
									{t('viewer.screenStream.presentedBy', { name: presenterName })}
								</div>
							)}
							<div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
								{/* A browser share never has the polling `isConnected` — read
								    the LiveKit room state instead (MU 5. 8.: header stuck on
								    "Nepřipojeno" while a share was playing). */}
								{liveKitSessionId ? (
									liveKitConnected ? (
										<Badge tone="success" className="text-sm" style={followedBadgeStyle}>
											<Video size={14} />
											{t('rooms.screenLiveShare')}
										</Badge>
									) : (
										<Badge tone="warning" className="text-sm" style={followedBadgeStyle}>
											{t('viewer.screenStream.connecting')}
										</Badge>
									)
								) : isConnected ? (
									<Badge
										tone="success"
										className="text-sm"
										style={followedBadgeStyle}
										title={
											framePoll
												? t('viewer.screen.mjpgFramePollHint')
												: isMjpgFallback
													? t('viewer.screen.mjpgFallbackHint')
													: undefined
										}
									>
										{getFormatIcon(framePoll ? ScreenFormat.JPG : settings.format)}
										{framePoll
											? t('viewer.screen.mjpgFramePollBadge')
											: isMjpgFallback
												? t('viewer.screen.mjpgFallbackBadge', { format: settings.format })
												: settings.format}
									</Badge>
								) : (
									<Badge tone="error" className="text-sm" style={followedBadgeStyle}>
										{t('localSources.disconnected')}
									</Badge>
								)}
								{/* MU 4. 9.: on a slide-style JPG the picture looks identical
								    paused or not, so without this the working Play/Pause read
								    as dead too. */}
								{canPause && !isPlaying && (
									<Badge tone="warning" className="text-sm" style={followedBadgeStyle}>
										<Pause size={14} />
										{t('viewer.screenStream.paused')}
									</Badge>
								)}
								{/* Inline error badge only when there's still content visible — otherwise
								    the full-area error state below carries the message. */}
								{activeError && hasDecodedFrame && (
									<Badge tone="warning" style={followedBadgeStyle}>
										{activeError}
									</Badge>
								)}
								{/* `opacity-60` rather than `text-base-content/60`: the muted tone
								    has to survive a followed foreground, and a fixed theme colour
								    would not (it could land dark-on-dark). */}
								{lastUpdate && (
									<span className="opacity-60 hidden sm:inline">
										{lastUpdate.toLocaleTimeString()}
									</span>
								)}
							</div>
						</div>
					</div>

					{/* Source selector - show when multiple screen sources exist (desktop only) */}
					{!isNative &&
						serviceInfo &&
						serviceInfo.siblingServices.filter((s: { type: string }) => isScreenLikeSource(s.type))
							.length > 1 && (
							<div className="order-last w-full sm:order-none sm:w-auto">
								<SourceSelector
									currentSourceId={sourceId ?? ''}
									sources={serviceInfo.siblingServices}
									room={serviceInfo.room}
									filterType={SourceType.SCREEN}
								/>
							</div>
						)}

					{/* Header controls — the transcript viewer's set and look (MU 5. 8.,
					    reopened 25. 8.): plain icon buttons with tooltips, no coloured
					    toggles. The zoom lock stays up here as well as in the settings
					    panel: the reviewer wants it one tap away while a lecture runs
					    (26. 8.). The proxy toggle is gone (#594) — the viewer picks
					    the route itself. */}
					<div className="flex gap-1 shrink-0 ml-auto">
						<button
							type="button"
							className={`p-2.5 rounded-lg transition-colors ${settings.zoomLocked ? 'bg-primary/20' : 'hover:bg-black/10'}`}
							onClick={() => setSettings((prev) => ({ ...prev, zoomLocked: !prev.zoomLocked }))}
							title={
								settings.zoomLocked ? t('viewer.screen.unlockZoom') : t('viewer.screen.lockZoom')
							}
							aria-label={
								settings.zoomLocked ? t('viewer.screen.unlockZoom') : t('viewer.screen.lockZoom')
							}
							aria-pressed={settings.zoomLocked}
						>
							{settings.zoomLocked ? <Lock size={20} /> : <LockOpen size={20} />}
						</button>
						{canPause && (
							<button
								type="button"
								className="p-2.5 rounded-lg hover:bg-black/10 transition-colors"
								onClick={() => setIsPlaying(!isPlaying)}
								title={t('viewer.keyboard.playPause')}
								aria-label={t('viewer.keyboard.playPause')}
								aria-pressed={isPlaying}
							>
								{isPlaying ? <Pause size={20} /> : <Play size={20} />}
							</button>
						)}
						<button
							type="button"
							className="p-2.5 rounded-lg hover:bg-black/10 transition-colors"
							onClick={toggleFullscreen}
							title={
								immersive || isFullscreen
									? t('viewer.screen.exitFullscreen')
									: t('viewer.keyboard.fullscreen')
							}
							aria-label={
								immersive || isFullscreen
									? t('viewer.screen.exitFullscreen')
									: t('viewer.keyboard.fullscreen')
							}
						>
							{immersive || isFullscreen ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
						</button>
						<button
							type="button"
							className={`p-2.5 rounded-lg transition-colors ${showSettings ? 'bg-primary/20' : 'hover:bg-black/10'}`}
							onClick={() => setShowSettings(!showSettings)}
							title={t('viewer.screen.settings.title')}
							aria-label={t('viewer.screen.settings.title')}
							aria-expanded={showSettings}
						>
							<Settings size={20} />
						</button>
					</div>
				</header>
			)}

			{/* In native landscape the container is a row, so the panel floats
			    over the picture instead of squeezing it from the side. It is
			    bounded to the viewport and scrolls inside itself: a taller panel
			    would otherwise scroll the whole document and carry the gear
			    toggle off-screen. The horizontal padding keeps the panel title
			    clear of the native back button (left) and the gear (right).
			    In portrait with an enlarged text size the panel grows taller than
			    the screen, so it is capped there too and scrolls inside itself;
			    otherwise its lower controls end up below the fold (MU, 7. 10. 2026). */}
			{showSettings && !immersive && (
				<div
					className={
						isNativeLandscape
							? `absolute inset-0 z-20 overflow-y-auto px-14 shadow-lg${followedColors ? '' : ' viewer-surface'}`
							: isTextEnlarged
								? 'min-h-0 max-h-[60dvh] overflow-y-auto'
								: ''
					}
					style={
						isNativeLandscape && followedColors
							? {
									backgroundColor: followedColors.backgroundColor,
									color: followedColors.textColor,
								}
							: undefined
					}
				>
					<ScreenSettingsPanel
						settings={settings}
						onChange={setSettings}
						useProxy={useProxy}
						onZoom={handleZoom}
						onClose={() => setShowSettings(false)}
						followedColors={followedColors}
						t={t}
					/>
				</div>
			)}

			{/* Main content wrapper — takes all remaining space */}
			<div className="flex-1 min-h-0 min-w-0 flex flex-col">
				{/* Image/Video viewer */}
				{/* biome-ignore lint/a11y/useKeyWithClickEvents: the pane must not become
				    a focus stop. Keyboard users reach the same immersive toggle through
				    the header button, F and Escape, wired above. */}
				<section
					ref={paneRef}
					{...surface(
						'relative flex-1 min-h-0 overflow-hidden rounded-lg screen-viewer touch-none',
					)}
					aria-label={t('viewer.screen.title')}
					onPointerDown={paneTap.onPointerDown}
					onClick={paneTap.onClick}
					onWheel={handleWheel}
					onTouchStart={handleTouchStart}
					onTouchMove={handleTouchMove}
					onTouchEnd={handleTouchEnd}
					// An interrupted gesture (incoming call, system edge swipe,
					// WebView scroll takeover) raises touchcancel INSTEAD of
					// touchend, and would otherwise leave the refs live.
					onTouchCancel={handleTouchEnd}
					onMouseDown={handleMouseDown}
					onMouseMove={handleMouseMove}
					onMouseUp={handleMouseUp}
					onMouseLeave={handleMouseUp}
				>
					{liveKitSessionId ? (
						// LiveKit-backed source owns its own loading/empty states inside
						// the pane (token fetch + room connection), so bypass the
						// JPEG-oriented loading gate which keys off `imageUrl`.
						renderContent()
					) : isLoading && !imageUrl ? (
						<div className="h-full flex flex-col items-center justify-center text-center px-6">
							<span className="text-primary mb-4">
								<Spinner size="lg" />
							</span>
							<p className="opacity-70">{t('viewer.screen.loading')}</p>
						</div>
					) : (
						renderContent()
					)}

					{/* Page-level errors win over everything — including the LiveKit
					    pane, which escalates fatal room errors here via `onFatalError`
					    precisely so viewers see this full-area state (with a login CTA
					    when auth is what failed) instead of waiting forever.

					    Gated on "is there a frame on screen", NOT on "did we build a
					    URL" as it was before: a URL is built for every proxied source,
					    so this state was unreachable for them and the actionable Czech
					    messages went to the header badge while the pane stayed blank.

					    An OVERLAY rather than a replacement, because replacing the
					    content unmounts the polling <img> — which is the only thing
					    that would ever notice the source coming back. Verified: as a
					    replacement, a camera that blipped for one second left the
					    student parked on "Try again" forever. Underneath, a polled
					    source keeps requesting and its next onLoad clears the failure
					    by itself. That self-healing is specific to polled sources: a
					    stream, or a fatal LiveKit error, produces no further events,
					    so those stay up until the user retries. */}
					{errorOwnsPane && (
						<div {...surface('absolute inset-0')}>
							<ScreenViewerErrorState
								error={activeError ?? ''}
								// Native: no room link at all — see ScreenViewerErrorState.
								// `|| isStandaloneViewer` so this can never disagree with the
								// `loginHref` gate beside it: if the bundle ever rendered with
								// standalone true and native false, this would be the only
								// button left, promoted to `primary` by the variant rule below
								// — and it is the exact dead end #647 removed.
								backHref={
									isNative || isStandaloneViewer
										? undefined
										: serviceInfo
											? viewerRoomPath(serviceInfo.room.spaceId, serviceInfo.room.id)
											: '/'
								}
								loginHref={loginHref}
								t={t}
							/>
						</div>
					)}
				</section>
			</div>
		</div>
	)
}

/**
 * Settings panel under the header — the transcript viewer's pattern. Holds
 * what the footer and the coloured header toggles used to: picture filters
 * and zoom with its lock. The connection column is read-only status, not a
 * control.
 */
function ScreenSettingsPanel({
	settings,
	onChange,
	useProxy,
	onZoom,
	onClose,
	followedColors,
	t,
}: {
	/** Colours inherited from the transcript viewer, or null to keep the theme. */
	followedColors: ViewerColorPair | null
	settings: ScreenViewerSettings
	onChange: (settings: ScreenViewerSettings) => void
	/** Read-only: the viewer decides the route itself (cloud service or an http
	 *  source on an https page → server; native WebView or loopback → direct).
	 *  A manual switch could only pick the wrong side, so it was dropped. */
	useProxy: boolean
	onZoom: (delta: number) => void
	onClose: () => void
	t: (key: string, options?: Record<string, unknown>) => string
}) {
	return (
		<section
			className={`border-b border-base-300 px-3 py-3 text-xl${followedColors ? '' : ' viewer-surface'}`}
			// The panel's labels and headings carry no explicit colour, so they
			// inherit whatever this section sets. It must therefore take the
			// followed PAIR or neither half.
			style={
				followedColors
					? {
							backgroundColor: followedColors.backgroundColor,
							color: followedColors.textColor,
						}
					: undefined
			}
			aria-labelledby="screen-settings-title"
		>
			<div className="max-w-4xl mx-auto">
				<div className="flex justify-between items-center gap-3 mb-2">
					<h2
						id="screen-settings-title"
						className="min-w-0 break-words hyphens-auto text-2xl font-semibold"
					>
						{t('viewer.screen.settings.title')}
					</h2>
					<button
						type="button"
						className="shrink-0 p-1 rounded hover:bg-black/10 min-h-[44px] min-w-[44px] flex items-center justify-center"
						onClick={onClose}
						title={t('viewer.screen.settings.close')}
						aria-label={t('viewer.screen.settings.close')}
					>
						<X size={18} />
					</button>
				</div>

				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
					<div className="space-y-1">
						<div className="break-words hyphens-auto text-xl font-medium">
							{t('viewer.screen.settings.picture')}
						</div>
						<label className="flex items-center gap-1.5 cursor-pointer min-h-[44px] break-words hyphens-auto">
							<input
								type="checkbox"
								className="checkbox-refined"
								checked={settings.grayscale}
								onChange={(e) => onChange({ ...settings, grayscale: e.target.checked })}
							/>
							{t('viewer.keyboard.grayscale')}
						</label>
						<label className="flex items-center gap-1.5 cursor-pointer min-h-[44px] break-words hyphens-auto">
							<input
								type="checkbox"
								className="checkbox-refined"
								checked={settings.inverted}
								onChange={(e) => onChange({ ...settings, inverted: e.target.checked })}
							/>
							{t('viewer.keyboard.inverted')}
						</label>
					</div>

					{/* 10 % per press, the same step as the keys and the wheel. With the
					    old 20 % a pinch to an odd tenth (130 %) could never get back to
					    100 % by the buttons (retest 24. 9. 2026, A6). clampZoom keeps
					    every value on a tenth, so 0.1 steps always hit 100 %. */}
					<div className="space-y-1">
						<div className="break-words hyphens-auto text-xl font-medium">
							{t('viewer.screen.settings.zoom')}
						</div>
						<div className="flex items-center gap-0.5">
							<button
								type="button"
								className="p-2.5 rounded-lg hover:bg-black/10 transition-colors disabled:opacity-40 disabled:hover:bg-transparent"
								onClick={() => onZoom(-0.1)}
								disabled={settings.zoomLocked}
								title={t('viewer.keyboard.zoomOut')}
								aria-label={t('viewer.keyboard.zoomOut')}
							>
								<ZoomOut size={20} />
							</button>
							<span className="min-w-[calc(3.25rem*var(--text-scale,1))] text-center tabular-nums">
								{Math.round(settings.zoomLevel * 100)}%
							</span>
							<button
								type="button"
								className="p-2.5 rounded-lg hover:bg-black/10 transition-colors disabled:opacity-40 disabled:hover:bg-transparent"
								onClick={() => onZoom(0.1)}
								disabled={settings.zoomLocked}
								title={t('viewer.keyboard.zoomIn')}
								aria-label={t('viewer.keyboard.zoomIn')}
							>
								<ZoomIn size={20} />
							</button>
						</div>
						<label className="flex items-center gap-1.5 cursor-pointer min-h-[44px] break-words hyphens-auto">
							<input
								type="checkbox"
								className="checkbox-refined"
								checked={settings.zoomLocked}
								onChange={(e) => onChange({ ...settings, zoomLocked: e.target.checked })}
							/>
							{t('viewer.screen.lockZoom')}
						</label>
					</div>

					<div className="space-y-1">
						<div className="break-words hyphens-auto text-xl font-medium">
							{t('viewer.screen.settings.connection')}
						</div>
						<p className="min-h-[44px] flex items-center break-words hyphens-auto">
							{useProxy
								? t('viewer.screen.settings.viaServer')
								: t('viewer.screen.settings.direct')}
						</p>
						<p className="break-words hyphens-auto text-lg">
							{t('viewer.screen.settings.connectionHint')}
						</p>
					</div>
				</div>
				<p className="mt-3 text-lg opacity-80 hidden lg:block">{t('viewer.screen.keyboardHint')}</p>
			</div>
		</section>
	)
}

function ScreenViewerErrorState({
	error,
	backHref,
	loginHref,
	t,
}: {
	error: string
	/**
	 * Where "back to room" goes, or undefined in a native WebView — the app's
	 * viewer is a MemoryRouter with a single route, so a /spaces link there
	 * unmounts the page and leaves a blank screen (#647).
	 */
	backHref?: string
	/** When set (UNAUTHORIZED), renders a primary "Log in" CTA → `/auth?returnTo=…`. */
	loginHref?: string
	t: (key: string, options?: Record<string, unknown>) => string
}) {
	return (
		<div className="h-full flex flex-col items-center justify-center text-center px-6">
			{/* `opacity-*` rather than `text-base-content/*` throughout this
			    state: it renders inside a surface that may be following the
			    transcript viewer's colours, and a fixed theme foreground would
			    land dark-on-dark — hiding the very message that explains what
			    went wrong. */}
			<WifiOff size={48} className="mb-4 opacity-40" />
			<h2 className="text-lg font-semibold mb-2">{t('viewer.screen.sourceUnavailable')}</h2>
			<p className="opacity-70 max-w-md mb-6">{error}</p>
			<div className="flex gap-2">
				{loginHref && (
					<Link to={loginHref}>
						<Button variant="primary" size="sm" leadingIcon={<LogIn size={16} />}>
							{t('viewer.screen.logIn')}
						</Button>
					</Link>
				)}
				{backHref && (
					<Link to={backHref}>
						<Button
							variant={loginHref ? 'ghost' : 'primary'}
							size="sm"
							leadingIcon={<ArrowLeft size={16} />}
						>
							{t('viewer.screen.backToRoom')}
						</Button>
					</Link>
				)}
				<Button
					variant="ghost"
					size="sm"
					onClick={() => window.location.reload()}
					leadingIcon={<RefreshCw size={16} />}
				>
					{t('viewer.transcript.tryAgain')}
				</Button>
			</div>
		</div>
	)
}
